import type {
  NetworkDashboardAvailableStore,
  NetworkDashboardStoreAgeFilter,
} from '@gozoki/api-types'
import { DateTime } from 'luxon'

/** Ancienneté UI state ↔ API `NetworkDashboardStoreAgeFilter` + optional explicit `ageStoreIds`. */
export type StoreAgeLocalState =
  | { mode: 'default' }
  | { mode: 'custom'; selectedStoreIds: Set<number> }

export interface NetworkDashboardAgeFilterApplyResult {
  storeAgeFilter: NetworkDashboardStoreAgeFilter
  /**
   * When set, the KPI request includes `ageStoreIds` and the backend intersects with geographic
   * `storeIds` instead of applying opening-date presets on SQL.
   */
  ageStoreIds: number[] | undefined
}

export function partitionStoresByOpeningMaturity(
  stores: NetworkDashboardAvailableStore[],
  periodEndIso: string,
): {
  lessThan3: NetworkDashboardAvailableStore[]
  between3And6: NetworkDashboardAvailableStore[]
  between6And12: NetworkDashboardAvailableStore[]
  moreThan12: NetworkDashboardAvailableStore[]
} {
  const threshold3 = DateTime.fromISO(periodEndIso).minus({ months: 3 }).toISODate()!
  const threshold6 = DateTime.fromISO(periodEndIso).minus({ months: 6 }).toISODate()!
  const threshold12 = DateTime.fromISO(periodEndIso).minus({ months: 12 }).toISODate()!

  const lessThan3: NetworkDashboardAvailableStore[] = []
  const between3And6: NetworkDashboardAvailableStore[] = []
  const between6And12: NetworkDashboardAvailableStore[] = []
  const moreThan12: NetworkDashboardAvailableStore[] = []

  for (const s of stores) {
    // Référence = date de première vente (repli ouverture), fournie par le backend.
    const ref = s.ageReferenceDate
    if (ref > threshold3) {
      lessThan3.push(s)
    } else if (ref <= threshold3 && ref > threshold6) {
      between3And6.push(s)
    } else if (ref <= threshold6 && ref > threshold12) {
      between6And12.push(s)
    } else {
      moreThan12.push(s)
    }
  }
  return { lessThan3, between3And6, between6And12, moreThan12 }
}

function setsEqual(a: Set<number>, b: Set<number>): boolean {
  if (a.size !== b.size) {
    return false
  }
  for (const id of a) {
    if (!b.has(id)) {
      return false
    }
  }
  return true
}

export function storeAgeFromFilter(
  filter: NetworkDashboardStoreAgeFilter,
  ageStoreIds: number[] | undefined,
  availableStores: NetworkDashboardAvailableStore[],
  periodEndIso: string,
): StoreAgeLocalState {
  if (filter === 'default') {
    return { mode: 'default' }
  }

  const parts = partitionStoresByOpeningMaturity(availableStores, periodEndIso)
  const lessThan3Ids = new Set(parts.lessThan3.map((s) => s.id))
  const between3And6Ids = new Set(parts.between3And6.map((s) => s.id))
  const between6And12Ids = new Set(parts.between6And12.map((s) => s.id))
  const moreThan12Ids = new Set(parts.moreThan12.map((s) => s.id))

  const fullIds = new Set<number>([
    ...lessThan3Ids,
    ...between3And6Ids,
    ...between6And12Ids,
    ...moreThan12Ids,
  ])

  if (ageStoreIds !== undefined) {
    return { mode: 'custom', selectedStoreIds: new Set(ageStoreIds) }
  }
  if (filter === 'all') {
    return { mode: 'custom', selectedStoreIds: fullIds }
  }
  if (filter === 'less_than_3_months') {
    return { mode: 'custom', selectedStoreIds: lessThan3Ids }
  }
  if (filter === 'between_3_and_6_months') {
    return { mode: 'custom', selectedStoreIds: between3And6Ids }
  }
  if (filter === 'between_6_and_12_months') {
    return { mode: 'custom', selectedStoreIds: between6And12Ids }
  }
  if (filter === 'more_than_12_months') {
    return { mode: 'custom', selectedStoreIds: moreThan12Ids }
  }
  return { mode: 'custom', selectedStoreIds: lessThan3Ids }
}

export function applyResultFromStoreAgeState(
  state: StoreAgeLocalState,
  availableStores: NetworkDashboardAvailableStore[],
  periodEndIso: string,
): NetworkDashboardAgeFilterApplyResult {
  if (state.mode === 'default') {
    return { storeAgeFilter: 'default', ageStoreIds: undefined }
  }

  const parts = partitionStoresByOpeningMaturity(availableStores, periodEndIso)
  const lessThan3Ids = new Set(parts.lessThan3.map((s) => s.id))
  const between3And6Ids = new Set(parts.between3And6.map((s) => s.id))
  const between6And12Ids = new Set(parts.between6And12.map((s) => s.id))
  const moreThan12Ids = new Set(parts.moreThan12.map((s) => s.id))

  const fullIds = new Set<number>([
    ...lessThan3Ids,
    ...between3And6Ids,
    ...between6And12Ids,
    ...moreThan12Ids,
  ])

  let selected = state.selectedStoreIds
  if (selected.size === 0) {
    selected = fullIds
  }

  if (setsEqual(selected, fullIds)) {
    return { storeAgeFilter: 'all', ageStoreIds: undefined }
  }
  if (setsEqual(selected, lessThan3Ids)) {
    return { storeAgeFilter: 'less_than_3_months', ageStoreIds: undefined }
  }
  if (setsEqual(selected, between3And6Ids)) {
    return { storeAgeFilter: 'between_3_and_6_months', ageStoreIds: undefined }
  }
  if (setsEqual(selected, between6And12Ids)) {
    return { storeAgeFilter: 'between_6_and_12_months', ageStoreIds: undefined }
  }
  if (setsEqual(selected, moreThan12Ids)) {
    return { storeAgeFilter: 'more_than_12_months', ageStoreIds: undefined }
  }
  return {
    storeAgeFilter: 'all',
    ageStoreIds: [...selected].sort((a, b) => a - b),
  }
}
